// Dependencies
import React, { useEffect, useState, useRef, useCallback } from "react";

// Modules
import { useSelector } from "react-redux";
import { useParams, useSearchParams } from "react-router-dom";

// App
import { client, serverUrl } from "../../config";
import { getOnlineNode } from "../../core/getNode";
import { alertMessages } from "../../partials/alertMessages";
import Error from "../../partials/error";
import SkeletonSingleScreen from "../../partials/skeleton-screens/skeletonSingleScreen";
import { logCustomEvent } from "../../core/analytics";
import PostBodyEdit from "../../partials/posts/postBodyEdit";
import { patchNode } from "../../core/postNode";
import MediaLibrary from "../../partials/mediaLibrary";
import { get_csrf_token } from "../../core/auth";
import PostEditToolbar from "./postEditToolbar";

// UI components
import { TextField, Typography, Button } from "@mui/material";
import { Loader } from "react-feather";
import { WarningAmber } from "@mui/icons-material";
import { Row, Col } from "react-bootstrap";

export default function PostEdit(props) {
  // Data

  const {
    apiEndpoint,
    screenClassName,
    returnRoute,
    backBtnText,
    previewRoute,
    logCustomEventType,
    setEntityPost,
    setEntityEditorContent,
    extraFields,
  } = props;

  const [post, setPost] = useState(null);
  const [initialData, setInitialData] = useState(null);
  const [csrf_token, setCsrfToken] = useState(null);

  // Editable fields
  const [postTitle, setPostTitle] = useState("");
  const [summary, setSummary] = useState("");
  const [featuredImageCaption, setFeaturedImageCaption] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [editorContent, setEditorContent] = useState("");
  const [reactionsEnabled, setReactionsEnabled] = useState(true);
  const [commentsEnabled, setCommentsEnabled] = useState(true);

  // Status
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [errorStatus, setErrorStatus] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isSaveError, setIsSaveError] = useState(false);
  const [resetKey, setResetKey] = useState(0);
  const [showMediaLibrary, setShowMediaLibrary] = useState(false);
  const [featuredImage, setFeaturedImage] = useState(null);
  const [featuredImageMid, setFeaturedImageMid] = useState(null);
  const [featuredImageCleared, setFeaturedImageCleared] = useState(false);

  const user = useSelector((state) => state.authReducer.user);
  const params = useParams();
  const [searchParams] = useSearchParams();
  const hash = searchParams.get("hash");

  const queueReloadRef = useRef(false);

  useEffect(() => {
    get_csrf_token().then((response) => setCsrfToken(response.data));
    loadNode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const debounceTimer = useRef(null);
  useEffect(() => {
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, []);

  useEffect(() => {
    if (!post) return; // skip on initial load
    setIsSaving(true);
    setIsSaveError(false);
    const featuredImageData =
      props.featuredImageEnabled && featuredImageMid
        ? {
            field_featured_image: {
              target_id: featuredImageMid,
              caption: featuredImageCaption,
            },
          }
        : props.featuredImageEnabled && featuredImageCleared
          ? { field_featured_image: { target_id: null } }
          : {};
    debouncedUpdate({
      title: postTitle,
      summary,
      body: editorContent,
      field_category: [selectedCategory],
      field_allow_reactions: reactionsEnabled,
      field_allow_comments: commentsEnabled,
      ...featuredImageData,
      ...extraFields,
    });
    // eslint-disable-next-line
  }, [
    postTitle,
    summary,
    featuredImageCaption,
    editorContent,
    reactionsEnabled,
    commentsEnabled,
    selectedCategory,
    extraFields,
    featuredImageMid,
    featuredImageCleared,
  ]);

  // ─── Data fetching ────────────────────────────────────────────────────────

  const loadNode = () => {
    const id = params.id;
    if (id) {
      getContent(id);
    } else {
      setError(true, 0, alertMessages.contentError);
    }
  };

  const spreadPostData = (data) => {
    if (setEntityPost) {
      setEntityPost(data);
    }
    setPost(data);
    setPostTitle(data.title);
    setSummary(data.summary ?? "");
    setEditorContent(data.body);
    setFeaturedImageMid(data.field_featured_image_id);
    setFeaturedImageCaption(data.field_featured_image_caption);
    if (setEntityEditorContent) {
      setEntityEditorContent(data.body);
    }
    setSelectedCategory(parseInt(data.category_ids));
    setReactionsEnabled(data.field_allow_reactions);
    setCommentsEnabled(data.field_allow_comments);
    if (props.featuredImageEnabled) {
      setFeaturedImage(data.field_featured_image);
    }
    setFeaturedImageCleared(false);
  };

  const getContent = (id) => {
    let path;

    if (client === "companyapp") {
      path = `${apiEndpoint}/${id}?_format=json`;
      if (hash) path += `&auHash=${hash}`;
    } else {
      path = `${apiEndpoint}/all/${id}?_format=json&status=1&promote=1`;
      if (hash) path = `/api/v2/shared?bundle=video&nid=${id}&authkey=${hash}`;
    }

    getOnlineNode(path, user.access_token)
      .then((response) => {
        if (response.status === 200) {
          setPost(response.data);
          spreadPostData(response.data);
          if (logCustomEventType) {
            logCustomEvent("read", response.data, logCustomEventType);
          }
          if (!initialData) setInitialData(response.data);
          setIsError(false);
        } else {
          setError(true, 404, "No data");
        }
      })
      .catch((_error) => {
        if (_error?.response?.status === 403) {
          setError(true, 403, "You do not have access to view this content.");
        } else {
          setError(true, 500, "Failed to get data");
        }
      })
      .finally(() => setIsLoading(false));
  };

  const setError = (_isError, _errorStatus, _errorMessage) => {
    setIsLoading(false);
    setIsError(_isError);
    setErrorStatus(_errorStatus);
    setErrorMessage(_errorMessage);
  };

  // ─── Actions ──────────────────────────────────────────────────────────────

  const debouncedUpdate = useCallback(
    (data) => {
      if (!csrf_token) {
        console.warn(
          "debouncedUpdate: csrf_token not yet available, skipping save",
        );
        setIsSaving(false);
        return;
      }
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => {
        patchNode(`${apiEndpoint}/${post.id}`, data, csrf_token)
          .then((response) => {
            setIsSaving(false);
            setIsSaveError(false);
            if (queueReloadRef.current) {
              setPost(response.data);
              spreadPostData(response.data);
              if (logCustomEventType) {
                logCustomEvent("read", response.data, logCustomEventType);
              }
              queueReloadRef.current = false;
            }
          })
          .catch((error) => {
            console.error("save error", error);
            setIsSaving(false);
            setIsSaveError(true);
          });
      }, 800);
    },
    // eslint-disable-next-line
    [post, csrf_token],
  );

  const revertChanges = () => {
    if (!initialData) return;
    spreadPostData(initialData);
    setPost(initialData);
    setResetKey((prev) => prev + 1);
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  if (isLoading) return <SkeletonSingleScreen />;
  if (isError) return <Error status={errorStatus} message={errorMessage} />;

  return (
    <main className={`${screenClassName} single post-edit`} data-id={post?.id}>
      <article className="article">
        <PostEditToolbar
          disableReactions={props.disableReactions}
          disableComments={props.disableComments}
          entityType={props.entityType}
          getContent={() => getContent(params.id)}
          route={returnRoute}
          backBtnText={backBtnText}
          previewRoute={
            props.previewOverride
              ? props.previewOverride
              : `${previewRoute}/${post.id}`
          }
          revertChanges={revertChanges}
          post={post}
        />
        <TextField
          className="post-title-edit"
          owner="client"
          value={postTitle}
          family="secondary"
          textvariant="h1"
          multiline
          maxRows={2}
          onInput={(e) => {
            queueReloadRef.current = true;
            setPostTitle(e.target.value);
          }}
        />
        {/* <TextField
          className="post-summary-edit w-100 mt-2"
          label="Summary"
          placeholder="Short summary shown in listings and previews"
          value={summary}
          owner="client"
          variant="standard"
          textvariant="body2"
          multiline
          onInput={(e) => {
            queueReloadRef.current = true;
            setSummary(e.target.value);
          }}
        /> */}
        {props.featuredImageEnabled && (
          <div className="post-featured-image mb-4">
            <Row className="post-featured-image-controls d-flex justify-content-end align-items-center">
              {featuredImage && (
                <Col xs="auto pe-0">
                  <Button
                    className="post-featured-image-remove"
                    variant="outlined"
                    component="label"
                    owner="admin"
                    color="error"
                    onClick={() => {
                      queueReloadRef.current = true;
                      setFeaturedImage(null);
                      setFeaturedImageMid(null);
                      setFeaturedImageCaption("");
                      setFeaturedImageCleared(true);
                    }}
                  >
                    <Typography owner="admin" variant="button" className="lh">
                      Remove featured image
                    </Typography>
                  </Button>
                </Col>
              )}
              <Col xs="auto">
                <Button
                  className="post-featured-image-btn"
                  variant="outlined"
                  component="label"
                  owner="admin"
                  color="primary"
                  onClick={() => {
                    setShowMediaLibrary(true);
                  }}
                >
                  <Typography owner="admin" variant="button" className="lh">
                    Select featured image
                  </Typography>
                </Button>
              </Col>
            </Row>
            {featuredImage && (
              <>
                <img
                  src={`${serverUrl}${featuredImage}`}
                  alt={`featured ${featuredImage}`}
                />
                <TextField
                  className="post-featured-image-caption w-100 mt-1"
                  label="Featured image caption"
                  value={featuredImageCaption}
                  owner="client"
                  variant="standard"
                  textvariant="body2"
                  onInput={(e) => {
                    setFeaturedImageCaption(e.target.value);
                  }}
                />
              </>
            )}
            <MediaLibrary
              user={user}
              files={
                props.featuredImageFiles
                  ? props.featuredImageFiles
                  : {
                      aspectRatio: 1.778,
                    }
              }
              csrf_token={csrf_token}
              mediaType={"image"}
              showMediaLibrary={showMediaLibrary}
              setShowMediaLibrary={setShowMediaLibrary}
              onMediaInsert={(media) => {
                queueReloadRef.current = true;
                setFeaturedImageMid(media.fid);
                setFeaturedImageCleared(false);
                setShowMediaLibrary(false);
              }}
            />
          </div>
        )}
        {props.children}
        {!props.editorDisabled && (
          <PostBodyEdit
            user={user}
            csrf_token={csrf_token}
            key={resetKey}
            postType={props.entityType}
            editorContent={editorContent}
            setEditorContent={(content) => {
              setEditorContent(content);
              if (setEntityEditorContent) {
                setEntityEditorContent(content);
              }
            }}
            editorProps={props.editorProps}
          />
        )}
        {isSaving || isSaveError ? (
          <div className="state-container">
            <Button className="state" disabled={true}>
              <span>
                {isSaving && !isSaveError && (
                  <span className="saver">
                    <Loader width={20} height={20} color="#1d202b" />
                    <span className="label">Saving</span>
                  </span>
                )}
                {!isSaving && isSaveError && (
                  <span className="error">
                    <WarningAmber fontSize={"small"} color={"error"} />
                    <span className="label">Error saving slide</span>
                  </span>
                )}
              </span>
            </Button>
          </div>
        ) : (
          <></>
        )}
      </article>
    </main>
  );
}
